import React from 'react'
import { Box, Checkbox, FormControl, IconButton, InputAdornment, ListItemText, MenuItem, Select, TextField, Typography } from '@mui/material'
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import ClearRoundedIcon from '@mui/icons-material/ClearRounded'
import { LoadingMaestro } from '../LoadingMaestro'

const toDateInputValue = (date = new Date()) => {
  const parsedDate = date instanceof Date ? date : new Date(date)

  if (Number.isNaN(parsedDate.getTime())) return ''

  const year = parsedDate.getFullYear()
  const month = String(parsedDate.getMonth() + 1).padStart(2, '0')
  const day = String(parsedDate.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

const getFechaCarga = (pedido) => pedido.fechaCarga || pedido.pedido?.cabecera?.fechaCarga

const getFechaCargaValue = (pedido) => {
  const fechaCarga = getFechaCarga(pedido)

  return fechaCarga ? toDateInputValue(fechaCarga) : ''
}

const getFechaCargaTime = (pedido) => {
  const fechaCarga = getFechaCarga(pedido)
  const parsedDate = fechaCarga ? new Date(fechaCarga) : null

  return parsedDate && !Number.isNaN(parsedDate.getTime())
    ? parsedDate.getTime()
    : Number.MAX_SAFE_INTEGER
}

const pedidoPendienteStatusStyles = {
  completed: {
    background: 'linear-gradient(135deg, #FFFFFF 0%, #DCFCE7 100%)',
    border: '1px solid #86EFAC',
    titleColor: '#14532D',
    metaColor: '#15803D',
    textColor: '#475569',
    shadow: '0 2px 6px rgba(22, 163, 74, 0.12)',
  },
  active: {
    background: 'linear-gradient(135deg, #FF9B22 0%, #F57C00 100%)',
    border: '1px solid #F57C00',
    titleColor: '#FFFFFF',
    metaColor: 'rgba(255, 255, 255, 0.92)',
    textColor: 'rgba(255, 255, 255, 0.86)',
    shadow: '0 2px 8px rgba(245, 124, 0, 0.24)',
  },
  delayed: {
    background: 'linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)',
    border: '1px solid #FCD34D',
    titleColor: '#713F12',
    metaColor: '#B45309',
    textColor: '#92400E',
    shadow: '0 2px 6px rgba(180, 83, 9, 0.12)',
  },
  waiting: {
    background: 'linear-gradient(135deg, #FFFFFF 0%, #F1F5F9 100%)',
    border: '1px solid #CBD5E1',
    titleColor: '#334155',
    metaColor: '#475569',
    textColor: '#64748B',
    shadow: '0 2px 6px rgba(71, 85, 105, 0.10)',
  },
  cancelled: {
    background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)',
    border: '1px dashed #F0A8A8',
    titleColor: '#475569',
    metaColor: '#9F4B4B',
    textColor: '#7F5D5D',
    shadow: '0 2px 6px rgba(185, 28, 28, 0.08)',
  },
}

export const PedidosPendientesPanel = ({
  variant = 'top',
  pedidosContraidos,
  setPedidosContraidos,
  loadingPedidos,
  pedidosPendientes,
  totalPedidosPendientes,
  gruposConfeccion = [],
  grupoConfeccionFiltro = [],
  setGrupoConfeccionFiltro,
  handleDropPedidosPendientes,
  getCantidadPales,
  getPaletsPlanificados,
  getPaletsProducidos,
  getPaletsPendientesPlanificar,
  setDragPayload,
  setDropPreview,
  setDetallePedido,
  onPedidoTouchPointerDown,
  puedeModificarPlanificacion = true,
}) => {
  const isSide = variant === 'side'
  const [busquedaPedidos, setBusquedaPedidos] = React.useState('')
  const [fechaDesdeFiltro, setFechaDesdeFiltro] = React.useState('')
  const [fechaHastaFiltro, setFechaHastaFiltro] = React.useState('')
  const gruposSeleccionados = React.useMemo(() => (
    Array.isArray(grupoConfeccionFiltro)
      ? grupoConfeccionFiltro
      : grupoConfeccionFiltro && grupoConfeccionFiltro !== 'todos'
        ? [grupoConfeccionFiltro]
        : []
  ), [grupoConfeccionFiltro])
  const gruposPorCodigo = React.useMemo(() => (
    gruposConfeccion.reduce((acc, grupo) => {
      acc[grupo.codGrupoConfeccion] = grupo.desGrupoConfeccion
      return acc
    }, {})
  ), [gruposConfeccion])
  const handleChangeGruposConfeccion = (event) => {
    const value = event.target.value
    const nuevosGrupos = typeof value === 'string' ? value.split(',') : value

    if (nuevosGrupos.includes('todos')) {
      setGrupoConfeccionFiltro?.([])
      return
    }

    setGrupoConfeccionFiltro?.(nuevosGrupos)
  }
  const resetFechaFiltro = () => {
    setFechaDesdeFiltro('')
    setFechaHastaFiltro('')
  }

  const pedidosOrdenados = React.useMemo(() => (
    [...pedidosPendientes].sort((pedidoA, pedidoB) => {
      const hoy = toDateInputValue()
      const fechaA = getFechaCargaValue(pedidoA)
      const fechaB = getFechaCargaValue(pedidoB)
      const pedidoAEsHoy = fechaA === hoy
      const pedidoBEsHoy = fechaB === hoy

      if (pedidoAEsHoy !== pedidoBEsHoy) return pedidoAEsHoy ? -1 : 1

      const fechaCompare = getFechaCargaTime(pedidoA) - getFechaCargaTime(pedidoB)

      if (fechaCompare !== 0) return fechaCompare

      const pedidoCompare = String(pedidoA.numPedido || '').localeCompare(
        String(pedidoB.numPedido || ''),
        'es',
        { numeric: true, sensitivity: 'base' },
      )

      if (pedidoCompare !== 0) return pedidoCompare

      return Number(pedidoA.numLinea || 0) - Number(pedidoB.numLinea || 0)
    })
  ), [pedidosPendientes])

  const pedidosFiltrados = React.useMemo(() => {
    const busquedaNormalizada = busquedaPedidos.trim().toLowerCase()
    const fechaInicio = fechaDesdeFiltro || fechaHastaFiltro
    const fechaFin = fechaHastaFiltro || fechaDesdeFiltro
    const pedidosPorFecha = fechaInicio || fechaFin
      ? pedidosOrdenados.filter((pedido) => {
        const fechaPedido = getFechaCargaValue(pedido)

        if (!fechaPedido) return false

        return (!fechaInicio || fechaPedido >= fechaInicio) && (!fechaFin || fechaPedido <= fechaFin)
      })
      : pedidosOrdenados

    if (!busquedaNormalizada) return pedidosPorFecha

    return pedidosPorFecha.filter((pedido) => [
      pedido.numPedido,
      pedido.numLinea,
      pedido.desVariedad,
      pedido.desConfeccion,
      pedido.desCalibre,
      pedido.desCliente,
      pedido.fechaCarga,
      pedido.desDirEnvio,
      pedido.desEnvase,
      pedido.desPalet,
      pedido.desMarca,
      pedido.desCategoria,
      pedido.pedido?.cabecera?.desDirEnvio,
      pedido.pedido?.linea?.desEnvase,
      pedido.pedido?.linea?.desPalet,
      pedido.pedido?.linea?.desMarca,
      pedido.pedido?.linea?.desCategoria,
    ].some((valor) => String(valor || '').toLowerCase().includes(busquedaNormalizada)))
  }, [busquedaPedidos, fechaDesdeFiltro, fechaHastaFiltro, pedidosOrdenados])

  const textoContador = loadingPedidos
    ? 'Cargando...'
    : busquedaPedidos.trim() || fechaDesdeFiltro || fechaHastaFiltro || gruposSeleccionados.length > 0
      ? `${pedidosFiltrados.length}/${totalPedidosPendientes ?? pedidosPendientes.length} lineas`
      : `${pedidosPendientes.length}/${totalPedidosPendientes ?? pedidosPendientes.length} lineas`

  return (
    <Box
      onDragOver={(event) => event.preventDefault()}
      onDrop={handleDropPedidosPendientes}
      sx={{
        position: isSide ? 'absolute' : 'relative',
        top: isSide ? 0 : 'auto',
        left: isSide ? 'auto' : 'auto',
        right: isSide ? 0 : 'auto',
        bottom: isSide ? 0 : 'auto',
        zIndex: isSide ? 15 : 'auto',
        width: isSide
          ? pedidosContraidos
            ? 58
            : { xs: 'calc(100% - 20px)', md: '50%', xl: 680 }
          : 'auto',
        minWidth: isSide && !pedidosContraidos ? { md: 420 } : 'auto',
        maxWidth: isSide && !pedidosContraidos ? { xs: 'calc(100% - 20px)', xl: 720 } : 'none',
        height: isSide ? '100%' : 'auto',
        px: isSide ? (pedidosContraidos ? 0.6 : { xs: 1, md: 1.25 }) : { xs: 1.5, md: 2 },
        py: isSide ? 1 : 0.75,
        borderLeft: isSide ? '1px solid #DEE6F2' : 'none',
        borderBottom: isSide ? 'none' : '1px solid #E6ECF5',
        backgroundColor: '#FFFFFF',
        boxShadow: isSide && !pedidosContraidos ? '-10px 0 28px rgba(15, 23, 42, 0.14)' : 'none',
        boxSizing: 'border-box',
        overflow: 'hidden',
        display: isSide ? 'flex' : 'block',
        flexDirection: 'column',
        transition: 'width 220ms ease, max-width 220ms ease, box-shadow 220ms ease, padding 220ms ease',
        willChange: isSide ? 'width' : 'auto',
      }}
    >
      <Box sx={{
        display: 'flex',
        alignItems: isSide && !pedidosContraidos ? 'flex-start' : 'center',
        justifyContent: 'space-between',
        gap: 1,
        mb: pedidosContraidos ? 0 : 0.75,
        flexDirection: isSide && !pedidosContraidos ? 'column' : 'row',
        transition: 'margin-bottom 180ms ease',
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: isSide ? '100%' : 'auto',
          gap: 1,
        }}>
        {!isSide || !pedidosContraidos ? (
          <Typography sx={{ color: '#172033', fontWeight: 800, fontSize: '0.84rem', textTransform: 'uppercase' }}>
          Pedidos pendientes
          </Typography>
        ) : (
          <Typography
            sx={{
              color: '#172033',
              fontWeight: 900,
              fontSize: '0.68rem',
              textTransform: 'uppercase',
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
              mx: 'auto',
              my: 1,
              letterSpacing: '0.08em',
            }}
          >
            Pendientes
          </Typography>
        )}

          <IconButton
            size="small"
            onClick={() => setPedidosContraidos((contraido) => !contraido)}
            title={pedidosContraidos ? 'Mostrar pedidos pendientes' : 'Ocultar pedidos pendientes'}
            sx={{
              width: 28,
              height: 28,
              border: '1px solid #DEE6F2',
              backgroundColor: '#FFFFFF',
              flexShrink: 0,
            }}
          >
            {pedidosContraidos ? <ChevronLeftRoundedIcon fontSize="small" /> : <ChevronRightRoundedIcon fontSize="small" />}
          </IconButton>
        </Box>

        <Box sx={{
          display: pedidosContraidos ? 'none' : 'flex',
          alignItems: 'center',
          gap: 0.75,
          flex: 1,
          minWidth: 0,
          width: isSide ? '100%' : 'auto',
          justifyContent: isSide ? 'flex-start' : 'flex-end',
          flexWrap: isSide ? 'wrap' : 'nowrap',
          opacity: pedidosContraidos ? 0 : 1,
          transform: pedidosContraidos ? 'translateX(12px)' : 'translateX(0)',
          transition: 'opacity 180ms ease 60ms, transform 180ms ease 60ms',
        }}>
          {!pedidosContraidos && (
            <>
              <TextField
                size="small"
                value={busquedaPedidos}
                onChange={(event) => setBusquedaPedidos(event.target.value)}
                placeholder="Buscar pedido..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon sx={{ fontSize: 16, color: '#94A3B8' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: isSide ? { xs: '100%', sm: 220 } : { xs: 140, sm: 210 },
                  '& .MuiInputBase-root': {
                    height: 30,
                    borderRadius: '7px',
                    fontSize: '0.76rem',
                    backgroundColor: '#FFFFFF',
                  },
                  '& .MuiInputBase-input': {
                    py: 0,
                  },
                }}
              />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, width: isSide ? { xs: '100%', sm: 'auto' } : 'auto' }}>
                <Box
                  component="input"
                  type="date"
                  value={fechaDesdeFiltro}
                  max={fechaHastaFiltro || undefined}
                  onChange={(event) => setFechaDesdeFiltro(event.target.value)}
                  title="Fecha de carga desde"
                  sx={{
                    height: 30,
                    width: isSide ? { xs: 'calc(50% - 21px)', sm: 132 } : { xs: 118, sm: 132 },
                    px: 0.65,
                    borderRadius: '7px',
                    border: '1px solid #CBD5E1',
                    backgroundColor: '#FFFFFF',
                    color: fechaDesdeFiltro ? '#172033' : '#64748B',
                    fontFamily: 'inherit',
                    fontSize: '0.7rem',
                    fontWeight: 800,
                    outline: 'none',
                    boxSizing: 'border-box',
                    '&:focus': {
                      borderColor: '#F57C00',
                      boxShadow: '0 0 0 3px rgba(245, 124, 0, 0.14)',
                    },
                  }}
                />

                <Typography sx={{ color: '#94A3B8', fontWeight: 900, fontSize: '0.68rem' }}>
                  -
                </Typography>

                <Box
                  component="input"
                  type="date"
                  value={fechaHastaFiltro}
                  min={fechaDesdeFiltro || undefined}
                  onChange={(event) => setFechaHastaFiltro(event.target.value)}
                  title="Fecha de carga hasta"
                  sx={{
                    height: 30,
                    width: isSide ? { xs: 'calc(50% - 21px)', sm: 132 } : { xs: 118, sm: 132 },
                    px: 0.65,
                    borderRadius: '7px',
                    border: '1px solid #CBD5E1',
                    backgroundColor: '#FFFFFF',
                    color: fechaHastaFiltro ? '#172033' : '#64748B',
                    fontFamily: 'inherit',
                    fontSize: '0.7rem',
                    fontWeight: 800,
                    outline: 'none',
                    boxSizing: 'border-box',
                    '&:focus': {
                      borderColor: '#F57C00',
                      boxShadow: '0 0 0 3px rgba(245, 124, 0, 0.14)',
                    },
                  }}
                />

                <IconButton
                  size="small"
                  onClick={resetFechaFiltro}
                  disabled={!fechaDesdeFiltro && !fechaHastaFiltro}
                  title="Limpiar rango de fechas"
                  sx={{
                    width: 30,
                    height: 30,
                    border: '1px solid #CBD5E1',
                    borderRadius: '7px',
                    backgroundColor: '#FFFFFF',
                    color: '#64748B',
                    '&:hover': {
                      borderColor: '#F57C00',
                      color: '#F57C00',
                      backgroundColor: '#FFF7ED',
                    },
                    '&.Mui-disabled': {
                      color: '#CBD5E1',
                      backgroundColor: '#F8FAFC',
                    },
                  }}
                >
                  <ClearRoundedIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Box>

              <FormControl
                size="small"
                sx={{
                  flex: { xs: '0 0 150px', sm: '1 1 260px' },
                  maxWidth: isSide ? { xs: '100%', sm: 360 } : { xs: 170, sm: 360 },
                  minWidth: isSide ? { xs: '100%', sm: 220 } : { xs: 150, sm: 220 },
                  '& .MuiInputBase-root': {
                    height: 30,
                    borderRadius: '7px',
                    fontSize: '0.74rem',
                    fontWeight: 800,
                    backgroundColor: '#FFFFFF',
                  },
                  '& .MuiSelect-select': {
                    py: 0,
                  },
                }}
              >
                <Select
                  multiple
                  value={gruposSeleccionados}
                  onChange={handleChangeGruposConfeccion}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected.length) return 'Todas confecciones'

                    return selected
                      .map((codigo) => gruposPorCodigo[codigo] ? `${codigo}` : codigo)
                      .join(', ')
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 320,
                      },
                    },
                  }}
                >
                  <MenuItem
                    value="todos"
                    onClick={() => setGrupoConfeccionFiltro?.([])}
                    sx={{ fontSize: '0.8rem', fontWeight: 800 }}
                  >
                    Todas confecciones
                  </MenuItem>
                  {gruposConfeccion.map((grupo) => (
                    <MenuItem key={grupo.codGrupoConfeccion} value={grupo.codGrupoConfeccion}>
                      <Checkbox
                        size="small"
                        checked={gruposSeleccionados.includes(grupo.codGrupoConfeccion)}
                        sx={{ p: 0.4, mr: 0.5 }}
                      />
                      <ListItemText
                        primary={`${grupo.codGrupoConfeccion} - ${grupo.desGrupoConfeccion}`}
                        primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 700 }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
          <Typography sx={{ color: '#64748B', fontWeight: 700, fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
            {textoContador}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          minHeight: 0,
          flex: isSide ? 1 : 'initial',
          display: isSide ? 'flex' : 'block',
          flexDirection: isSide ? 'column' : 'initial',
          opacity: pedidosContraidos ? 0 : 1,
          transform: pedidosContraidos ? 'translateX(14px)' : 'translateX(0)',
          transition: 'opacity 180ms ease 70ms, transform 180ms ease 70ms',
          overflow: isSide ? 'hidden' : 'visible',
          pointerEvents: pedidosContraidos ? 'none' : 'auto',
        }}
      >
      {pedidosContraidos ? null : loadingPedidos ? (
      <Box sx={{ height: 82, display: 'grid', placeItems: 'center' }}>
        <LoadingMaestro label="Cargando pedidos pendientes..." />
      </Box>
    ) : pedidosPendientes.length === 0 ? (
      <Box
        sx={{
          height: isSide ? '100%' : 74,
          borderRadius: '8px',
          border: '1px dashed #CBD5E1',
          display: 'grid',
          placeItems: 'center',
          color: '#64748B',
          fontWeight: 700,
          fontSize: '0.82rem',
        }}
      >
        Sin pedidos pendientes para el centro seleccionado
      </Box>
    ) : pedidosFiltrados.length === 0 ? (
      <Box
        sx={{
          height: isSide ? '100%' : 74,
          borderRadius: '8px',
          border: '1px dashed #CBD5E1',
          display: 'grid',
          placeItems: 'center',
          color: '#64748B',
          fontWeight: 700,
          fontSize: '0.82rem',
        }}
      >
        Sin pedidos pendientes que coincidan con la busqueda o la fecha
      </Box>
    ) : (
      <Box
        sx={{
          display: 'flex',
          flexDirection: isSide ? 'column' : 'row',
          gap: 1,
          overflowX: isSide ? 'hidden' : 'auto',
          overflowY: isSide ? 'auto' : 'hidden',
          pb: 0.25,
          pr: isSide ? 0.5 : 0,
          minHeight: 0,
          flex: isSide ? 1 : 'initial',
          maxHeight: isSide ? '100%' : 'none',
        }}
      >
        {pedidosFiltrados.map((pedido) => {
          const estadoPlanificador = pedido.__plannerStatus
          const pedidoPlanificado = Boolean(pedido.__estaPlanificado)
          const tone = pedidoPlanificado
            ? pedidoPendienteStatusStyles[estadoPlanificador] || pedidoPendienteStatusStyles.waiting
            : null
          const cantidadPalets = getCantidadPales(pedido)
          const paletsPendientesPlanificar = getPaletsPendientesPlanificar(pedido)
          const paletsMostrar = paletsPendientesPlanificar > 0 ? paletsPendientesPlanificar : cantidadPalets
          const puedePlanificar = puedeModificarPlanificacion && paletsPendientesPlanificar > 0
          const cabecera = pedido.pedido?.cabecera || {}
          const linea = pedido.pedido?.linea || {}
          const confeccion = pedido.pedido?.confeccion || {}
          const fechaCarga = pedido.fechaCarga || cabecera.fechaCarga
          const fechaCargaLabel = fechaCarga
            ? new Date(fechaCarga).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' })
            : '-'
          const desCliente = pedido.desCliente || cabecera.desCliente || '-'
          const desDirEnvio = pedido.desDirEnvio || cabecera.desDirEnvio || '-'
          const desVariedad = pedido.desVariedad || linea.desVariedad || '-'
          const desConfeccion = pedido.desConfeccion || confeccion.desConfeccion || linea.desConfeccion || '-'
          const desCalibre = pedido.desCalibre || linea.desCalibre || '-'
          const desEnvase = pedido.desEnvase || linea.desEnvase || '-'
          const desPalet = pedido.desPalet || linea.desPalet || '-'
          const desMarca = pedido.desMarca || linea.desMarca || '-'
          const desCategoria = pedido.desCategoria || linea.desCategoria || '-'
          const bultosPalet = pedido.bultosPalet ?? linea.bultosPalet ?? '-'
          const pedidoDragPayload = {
            type: 'pedido',
            numPedido: pedido.numPedido,
            numLinea: pedido.numLinea,
            cantidadPales: paletsPendientesPlanificar,
          }

          return (
            <Box
              key={`${pedido.numPedido}-${pedido.numLinea}`}
              data-planner-draggable="true"
              draggable={puedePlanificar}
              onPointerDown={(event) => {
                if (!puedePlanificar || event.pointerType === 'mouse') return

                onPedidoTouchPointerDown?.(event, pedidoDragPayload)
              }}
              onDragStart={(event) => {
                if (!puedePlanificar) {
                  event.preventDefault()
                  return
                }

                setDragPayload(event, pedidoDragPayload)
              }}
              onDragEnd={() => setDropPreview(null)}
              onDoubleClick={(event) => {
                event.stopPropagation()
                setDetallePedido({ tipo: 'pendiente', data: pedido })
              }}
              sx={{
                flex: isSide ? '0 0 auto' : '0 0 360px',
                width: isSide ? '100%' : 'auto',
                borderRadius: '8px',
                border: tone?.border || '1px solid #DEE6F2',
                background: tone?.background || '#FCFDFE',
                px: 0.95,
                py: 0.65,
                boxShadow: tone?.shadow || '0 2px 6px rgba(15, 23, 42, 0.06)',
                opacity: puedePlanificar || pedidoPlanificado ? 1 : 0.68,
                cursor: puedePlanificar ? 'grab' : 'default',
                touchAction: puedePlanificar ? 'pan-y' : 'auto',
                '&:active': {
                  cursor: puedePlanificar ? 'grabbing' : 'default',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.35 }}>
                <Typography sx={{ color: tone?.titleColor || '#F57C00', fontWeight: 900, fontSize: '0.74rem', lineHeight: 1, whiteSpace: 'nowrap' }}>
                  {pedido.numPedido}
                </Typography>
                <Typography sx={{ color: tone?.metaColor || '#172033', fontWeight: 900, fontSize: '0.68rem', lineHeight: 1, whiteSpace: 'nowrap' }}>
                  {fechaCargaLabel} | {paletsMostrar} palets | {bultosPalet} bultos
                </Typography>
              </Box>

              <Typography sx={{ color: tone?.titleColor || '#172033', fontWeight: 800, fontSize: '0.72rem', lineHeight: 1.05, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {desCliente} | {desDirEnvio}
              </Typography>

              <Typography sx={{ color: tone?.metaColor || '#475569', fontWeight: 800, fontSize: '0.68rem', lineHeight: 1.05, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', mt: 0.18 }}>
                {desVariedad} | {desConfeccion}
              </Typography>

              <Typography sx={{ color: tone?.textColor || '#64748B', fontSize: '0.64rem', lineHeight: 1.05, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', mt: 0.18 }}>
                Cal. {desCalibre} | {desEnvase}
              </Typography>

              <Typography sx={{ color: tone?.textColor || '#64748B', fontSize: '0.64rem', lineHeight: 1.05, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', mt: 0.18 }}>
                {desPalet} | Marca {desMarca} | {desCategoria}
              </Typography>
            </Box>
          )
        })}
      </Box>
    )}
      </Box>
    </Box>
  )
}
